import React from 'react';
import { Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import SignUpForm from './SignUpForm';
import ChooseRoleStep from './ChooseRoleStep';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { SignInErrorType } from '../../pages/Auth/reducer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CommunicationChannel,
  RegisterUserCommand,
} from '../../configuration/api/apiSchema';
import { connect } from 'react-redux';
import {
  checkDuplicateEmail,
  sendSignUpUser,
  sendSignUpUserOtp,
  signInUser,
  UserRoles,
  OtpErrorType,
} from '../../pages/Auth/actions';
import EnterOtpCodeStep from './EnterOtpCodeStep';
import {
  GlassContainer,
  GlassCard,
  ButtonTertiaryIcon,
} from '../glassmorphism/GlassmorphismStyles';
import { ReactComponent as CloseIcon } from '../../assets/icons/nav/close.svg';
import { trackEvent } from '../../analytics';
import OnboardingSkeleton from '../skeletons/OnboardingSkeleton';

interface DispatchFromProps {
  sendSignUpUserOtp: (
    otpContactReference: string,
    otpCommunicationChannel: CommunicationChannel,
  ) => void;
  sendSignUpUser: (command: RegisterUserCommand) => void;
  signInUser: (username: string, password: string) => void;
  checkDuplicateEmail: (email: string) => void;
}

interface StateFromProps {
  isError: boolean;
  isLoading: boolean;
  errorType?: SignInErrorType | OtpErrorType;
  isSignUpSuccessfull: boolean;
  isOtpConfirmed: boolean;
  isAuthorized: boolean;
  isSignupLoading: boolean;
  isDuplicatedEmailError: boolean;
  isDuplicatedEmailChecked: boolean;
}

interface PassedProps {
  disableCustomTheme?: boolean;
}

enum SignUpSteps {
  ChooseRole = 1,
  SignUpForm = 2,
  ConfirmOtpCode = 3,
}

export type UserRoleType = 'handyman' | 'customer';

const SignUpFormWizzard = (
  props: PassedProps & DispatchFromProps & StateFromProps,
) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = React.useState<SignUpSteps>(
    SignUpSteps.ChooseRole,
  );
  const [userRoleId, setUserRoleId] = React.useState<UserRoles>(
    UserRoles.CustomerRoleId,
  );
  const [user, setUser] = React.useState<RegisterUserCommand>({});
  const [selectedRole, setSelectedRole] = React.useState<
    'handyman' | 'customer'
  >('customer');
  // Destructure props to avoid exhaustive-deps warnings
  const {
    isSignUpSuccessfull,
    isAuthorized,
    isDuplicatedEmailChecked,
    isDuplicatedEmailError,
    signInUser,
    sendSignUpUserOtp,
  } = props;
  // Handle pre-selected role from URL params (e.g., /sign-up?role=handyman)
  const roleParam = searchParams.get('role');
  React.useEffect(() => {
    if (roleParam === 'handyman' || roleParam === 'customer') {
      trackEvent('sign_up_started', { role: roleParam, source: 'url' });
      setSelectedRole(roleParam);
      setUserRoleId(
        roleParam === 'handyman'
          ? UserRoles.HandymanRoleId
          : UserRoles.CustomerRoleId,
      );
      // Skip role selection step and go directly to signup form
      setCurrentStep(SignUpSteps.SignUpForm);
    }
  }, [roleParam]);

  React.useEffect(() => {
    if (isSignUpSuccessfull) {
      trackEvent('sign_up_completed', { role: selectedRole });
      signInUser(user.email as string, user.password as string);
    }
  }, [
    isSignUpSuccessfull,
    signInUser,
    user.email,
    user.password,
    selectedRole,
  ]);

  React.useEffect(() => {
    if (isAuthorized && selectedRole === 'customer') {
      navigate('/profile');
    } else if (isAuthorized && selectedRole === 'handyman') {
      // Redirect handymen to category selection after successful registration
      navigate('/select-categories');
    }
  }, [isAuthorized, selectedRole, navigate]);

  React.useEffect(() => {
    if (isDuplicatedEmailChecked && !isDuplicatedEmailError) {
      sendSignUpUserOtp(user.email as string, CommunicationChannel.Email);

      setCurrentStep(SignUpSteps.ConfirmOtpCode);
    }
  }, [
    isDuplicatedEmailChecked,
    isDuplicatedEmailError,
    sendSignUpUserOtp,
    user.email,
  ]);

  const onRoleSelect = (role: UserRoleType) => {
    trackEvent('sign_up_started', { role });
    setSelectedRole(role);

    if (role === 'handyman') {
      setUserRoleId(UserRoles.HandymanRoleId);
    } else if (role === 'customer') {
      setUserRoleId(UserRoles.CustomerRoleId);
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('role', role);
        return next;
      },
      { replace: true },
    );

    // Both roles go to the sign-up form next
    setCurrentStep(SignUpSteps.SignUpForm);
  };

  // Handle form submission
  const onFormSubmit = (
    email: string,
    password: string,
    name: string,
    lastname: string,
    location?: string,
  ) => {
    trackEvent('sign_up_form_submitted', { role: selectedRole });
    props.checkDuplicateEmail(email);

    const userData: RegisterUserCommand = {
      email: email,
      password: password,
      firstName: name,
      lastName: lastname,
      otpCommunicationChannel: CommunicationChannel.Email,
      otpContactReference: email,
      roleId: userRoleId,
    };

    // Add address for handymen if provided (used to create service area)
    if (location && selectedRole === 'handyman') {
      userData.address = location;
    }

    setUser(userData);
  };

  // Handle OTP code submission
  const onOtpCodeSubmit = (otpCode: string) => {
    props.sendSignUpUser({
      ...user,
      otpCode: otpCode,
    });
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === SignUpSteps.SignUpForm) {
      setCurrentStep(SignUpSteps.ChooseRole);
    } else if (currentStep === SignUpSteps.ConfirmOtpCode) {
      setCurrentStep(SignUpSteps.SignUpForm);
    }
  };

  const [showSkeleton, setShowSkeleton] = React.useState(true);
  const [hasShownSkeleton, setHasShownSkeleton] = React.useState(false);

  React.useEffect(() => {
    if (!hasShownSkeleton) {
      const timer = setTimeout(() => {
        setShowSkeleton(false);
        setHasShownSkeleton(true);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [hasShownSkeleton]);
  const theme = useTheme();
  if (
    (props.isLoading || showSkeleton) &&
    currentStep === SignUpSteps.ChooseRole
  ) {
    return <OnboardingSkeleton />;
  }

  return (
    <AppTheme>
      <GlassContainer>
        <SignUpContainer sx={{ '&&': { pt: '24px' } }}>
          <GlassCard
            sx={{
              width: '100%',
              maxWidth: '956px',
              margin: '0 auto',
              padding: { xs: 0, sm: 4, md: 5 },
              position: 'relative',
              overflow: 'visible',
              '&:hover': {
                backgroundColor: 'unset',
                transform: 'none',
              },
              [theme.breakpoints.down('sm')]: {
                boxShadow: 'unset',
              },
            }}
          >
            <ButtonTertiaryIcon
              onClick={() => navigate(-1)}
              sx={{
                position: 'absolute',
                zIndex: 1,
                top: { xs: '-16px', sm: '32px' },
                right: { xs: 0, sm: '32px' },
              }}
            >
              <CloseIcon />
            </ButtonTertiaryIcon>
            {currentStep === SignUpSteps.ChooseRole && (
              <ChooseRoleStep
                onSelect={onRoleSelect}
                isHandyman={selectedRole === 'handyman'}
              />
            )}

            {currentStep === SignUpSteps.SignUpForm && (
              <SignUpForm
                onSubmit={onFormSubmit}
                onBack={handleBack}
                isLoading={props.isLoading}
                isDuplicatedEmailError={props.isDuplicatedEmailError}
                selectedRole={selectedRole}
                isHandyman={selectedRole === 'handyman'}
              />
            )}

            {currentStep === SignUpSteps.ConfirmOtpCode && (
              <EnterOtpCodeStep
                onSubmit={onOtpCodeSubmit}
                onBack={handleBack}
                email={user.email as string}
                isLoading={props.isLoading}
                errorType={props.errorType}
                isHandyman={selectedRole === 'handyman'}
              />
            )}
          </GlassCard>
        </SignUpContainer>
      </GlassContainer>
    </AppTheme>
  );
};

const SignUpContainer = styled(Box)(({ theme }) => ({
  // minHeight: '100vh',
  display: 'flex',
  // alignItems: 'center',
  justifyContent: 'center',
  padding: '12px',
  paddingTop: '100px', // Space for mobile header
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 92, 173, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0, 92, 173, 0.5)',
    },
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    paddingTop: '100px', // Space for mobile header
  },
  [theme.breakpoints.up('md')]: {
    paddingTop: '120px', // Space for desktop header
  },
}));

const mapStateToProps = (state: HandymanAppState): StateFromProps => {
  return {
    isError: state.user.isError,
    isLoading: state.user.isLoading,
    errorType: state.user.errorType,
    isSignUpSuccessfull: state.user.isSignUpSuccessfull,
    isOtpConfirmed: state.user.isOtpConfirmed,
    isAuthorized: state.user.isAuthorized,
    isSignupLoading: state.user.isSignupLoading,
    isDuplicatedEmailError: state.user.isDuplicatedEmailError,
    isDuplicatedEmailChecked: state.user.isDuplicatedEmailChecked,
  };
};

export default connect<
  StateFromProps,
  DispatchFromProps,
  any,
  HandymanAppState
>(mapStateToProps, {
  sendSignUpUserOtp: sendSignUpUserOtp,
  sendSignUpUser: sendSignUpUser,
  signInUser: signInUser,
  checkDuplicateEmail: checkDuplicateEmail,
})(SignUpFormWizzard);
